import { AUTH0_CLAIM_REFRESH_STORAGE_KEY } from '../constants/localStorage';
import { refreshAuth0AccessToken } from './auth0TokenRefresh';
import { isAccessTokenAuth0 } from './cookies';
import { isLastActiveExpired } from './handleSession';
import { logger } from './logger';

/** Dispatched on `window` after a refresh replaced the cached Auth0 claims in this tab. */
export const AUTH0_CLAIMS_REFRESHED_DOM_EVENT = 'ecom:auth0-claims-refreshed';

/**
 * Cooldown applied after each unresolved attempt, in milliseconds: 2s, 4s, 8s, 16s, 32s, 60s.
 * The last entry repeats, so a claim that never arrives settles into one attempt every
 * minute across the whole browser.
 */
const BACKOFF_MS = [2_000, 4_000, 8_000, 16_000, 32_000, 60_000];

/**
 * Cooldown written when reserving the slot, before Auth0 is called. Tabs that read the state
 * while the refresh is in flight back off instead of issuing a second `/oauth/token` call.
 */
const RESERVATION_COOLDOWN_MS = 15_000;

/**
 * Cooldown reported when the session is not eligible *yet*. `Auth0TokenSync` writes the Auth0
 * access cookie from its own async effect, so a claim sync that runs first sees no Auth0
 * session at all; without a retry the pending claim would stay stale for the whole visit.
 * Matches the first unresolved back-off so the cookie race is retried as soon as the
 * remint loop would have anyway.
 */
const NOT_YET_ELIGIBLE_RETRY_MS = BACKOFF_MS[0];

/** Settling time before re-reading the reservation to see which tab's write landed last. */
const RESERVATION_ARBITRATION_MS = 50;

type Auth0ClaimRefreshState = {
    /** `sub` of the session the counters belong to, so switching accounts starts clean. */
    subject: string;
    attempts: number;
    nextAttemptAt: number;
    owner: string;
    reservedAt: number;
    /** Bumped only when a refresh actually replaced the claims; other tabs watch this. */
    refreshedAt: number;
};

export type Auth0ClaimsRefreshStatus = 'refreshed' | 'unresolved' | 'throttled' | 'skipped';

export type Auth0ClaimsRefreshResult = {
    status: Auth0ClaimsRefreshStatus;
    /** Delay after which another attempt is allowed. Absent when nothing is worth retrying. */
    retryAfterMs?: number;
};

export type Auth0ClaimsRefreshOptions = {
    /** `sub` of the session whose claims are stale. */
    subject: string;
    /** Re-reads the claims after the refresh and reports whether the awaited value arrived. */
    hasClaimResolved: () => Promise<boolean> | boolean;
};

const SKIPPED: Auth0ClaimsRefreshResult = { status: 'skipped' };
const SKIPPED_NOT_YET_ELIGIBLE: Auth0ClaimsRefreshResult = {
    status: 'skipped',
    retryAfterMs: NOT_YET_ELIGIBLE_RETRY_MS,
};

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

let inFlight: Promise<Auth0ClaimsRefreshResult> | null = null;

function isNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function parseState(raw: string | null): Auth0ClaimRefreshState | undefined {
    if (!raw) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(raw) as Partial<Auth0ClaimRefreshState>;
        if (
            typeof parsed?.subject !== 'string' ||
            !isNumber(parsed.attempts) ||
            !isNumber(parsed.nextAttemptAt) ||
            typeof parsed.owner !== 'string' ||
            !isNumber(parsed.reservedAt) ||
            !isNumber(parsed.refreshedAt)
        ) {
            return undefined;
        }

        return parsed as Auth0ClaimRefreshState;
    } catch {
        return undefined;
    }
}

function readState(): Auth0ClaimRefreshState | undefined {
    try {
        return parseState(globalThis.window?.localStorage.getItem(AUTH0_CLAIM_REFRESH_STORAGE_KEY) ?? null);
    } catch {
        // Storage can throw in private browsing modes; treat it as "no cooldown recorded".
        return undefined;
    }
}

function writeState(state: Auth0ClaimRefreshState): void {
    try {
        globalThis.window?.localStorage.setItem(AUTH0_CLAIM_REFRESH_STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Without persistence the throttle degrades to the per-tab in-flight guard, which is
        // still enough to keep a single tab from looping on `/oauth/token`.
    }
}

function delay(ms: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}

/**
 * A refresh is only worth attempting for a live, client-owned Auth0 session in a visible tab.
 *
 * `'ineligible'` ends the work: an expired session stays expired, and a hidden tab is skipped so
 * a pile of open tabs cannot multiply the request rate — it resumes from `visibilitychange`.
 * A missing Auth0 access cookie is `'not-yet-eligible'` instead, because it is usually the token
 * sync still writing it, and nothing else would bring the caller back.
 */
function getRefreshEligibility(): 'eligible' | 'not-yet-eligible' | 'ineligible' {
    if (globalThis.window === undefined || isLastActiveExpired() || globalThis.document?.visibilityState === 'hidden') {
        return 'ineligible';
    }

    return isAccessTokenAuth0() ? 'eligible' : 'not-yet-eligible';
}

function backoffFor(attempts: number): number {
    return BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length) - 1];
}

function toRetryAfter(nextAttemptAt: number): number {
    return Math.max(nextAttemptAt - Date.now(), 0);
}

async function executeClaimsRefresh({
    subject,
    hasClaimResolved,
}: Auth0ClaimsRefreshOptions): Promise<Auth0ClaimsRefreshResult> {
    const stored = readState();
    // Counters belong to the session that recorded them: a different `sub` starts from scratch.
    const current = stored?.subject === subject ? stored : undefined;

    if (current && Date.now() < current.nextAttemptAt) {
        return { status: 'throttled', retryAfterMs: toRetryAfter(current.nextAttemptAt) };
    }

    const reservation: Auth0ClaimRefreshState = {
        subject,
        attempts: current?.attempts ?? 0,
        nextAttemptAt: Date.now() + RESERVATION_COOLDOWN_MS,
        owner: tabId,
        reservedAt: Date.now(),
        refreshedAt: current?.refreshedAt ?? 0,
    };
    writeState(reservation);

    // Two tabs can read an elapsed cooldown in the same tick and both reserve. The SPA SDK
    // serialises the calls but does not re-check its cache with `cacheMode: 'off'`, so both
    // would spend a rotating refresh token. Let the writes settle and defer to the last one.
    await delay(RESERVATION_ARBITRATION_MS);
    const confirmed = readState();
    if (confirmed && confirmed.owner !== tabId) {
        return { status: 'throttled', retryAfterMs: toRetryAfter(confirmed.nextAttemptAt) };
    }

    let hasResolved = false;
    let accessToken: string | null = null;
    try {
        accessToken = await refreshAuth0AccessToken();
        hasResolved = Boolean(accessToken) && (await hasClaimResolved());
    } catch (error) {
        logger.error('Error refreshing Auth0 claims', error);
    }

    if (hasResolved) {
        writeState({ ...reservation, attempts: 0, nextAttemptAt: 0, refreshedAt: Date.now() });
        globalThis.window?.dispatchEvent(new CustomEvent(AUTH0_CLAIMS_REFRESHED_DOM_EVENT));
        return { status: 'refreshed' };
    }

    const attempts = reservation.attempts + 1;
    const retryAfterMs = backoffFor(attempts);
    writeState({ ...reservation, attempts, nextAttemptAt: Date.now() + retryAfterMs });

    return { status: 'unresolved', retryAfterMs };
}

/**
 * Re-mints the Auth0 tokens so claims written to `app_metadata` after login reach the browser.
 *
 * The token endpoint is expensive and rate-limited, and the claim may never arrive, so attempts
 * are throttled through `localStorage`: the cooldown is shared by every tab and survives reloads,
 * and the slot is reserved before Auth0 is called so tabs that start together do not both spend
 * the rotating refresh token. `hasClaimResolved` decides whether the counters reset or back off.
 *
 * Returns `retryAfterMs` so callers can schedule the next attempt without duplicating the policy.
 */
export async function runThrottledAuth0ClaimsRefresh(
    options: Auth0ClaimsRefreshOptions
): Promise<Auth0ClaimsRefreshResult> {
    const eligibility = getRefreshEligibility();
    if (eligibility !== 'eligible') {
        return eligibility === 'not-yet-eligible' ? SKIPPED_NOT_YET_ELIGIBLE : SKIPPED;
    }

    if (inFlight) {
        return inFlight;
    }

    inFlight = (async () => {
        try {
            return await executeClaimsRefresh(options);
        } finally {
            inFlight = null;
        }
    })();

    return inFlight;
}

/**
 * Notifies when refreshed Auth0 claims become available, in this tab and in every other one.
 *
 * The SDK reads the id token through its `localStorage` cache, so a tab that did not run the
 * refresh only needs to re-read its claims — no further network call.
 */
export function subscribeToAuth0ClaimsRefresh(listener: () => void): () => void {
    if (globalThis.window === undefined) {
        return () => {};
    }

    let lastSeenRefreshedAt = readState()?.refreshedAt ?? 0;

    const handleStorage = (event: StorageEvent) => {
        if (event.key !== AUTH0_CLAIM_REFRESH_STORAGE_KEY) {
            return;
        }

        // Reservations and back-off writes touch the same key without changing the claims.
        const refreshedAt = parseState(event.newValue)?.refreshedAt ?? 0;
        if (refreshedAt <= lastSeenRefreshedAt) {
            return;
        }

        lastSeenRefreshedAt = refreshedAt;
        listener();
    };

    globalThis.window.addEventListener(AUTH0_CLAIMS_REFRESHED_DOM_EVENT, listener);
    globalThis.window.addEventListener('storage', handleStorage);

    return () => {
        globalThis.window.removeEventListener(AUTH0_CLAIMS_REFRESHED_DOM_EVENT, listener);
        globalThis.window.removeEventListener('storage', handleStorage);
    };
}
